// Push Autofill UX (Phase 08 - 2026-08-14) common-fe pure logic.
//
// TWIN of qlvb-fe/src/utils/push-approval-login.js - KEEP IN SYNC.
// Kongming Counsel 2 accepted duplication (2 git repo độc lập, KHÔNG monorepo). Test cases IDENTICAL để bắt drift.
//
// Cross-reference:
//   - qlvb-fe/src/utils/push-approval-login.js (PascalCase reader via qlvb-be proxy)
//   - qlvb-fe/src/utils/push-approval-login.test.js (Node --test)
//
// KHÁC BIỆT DUY NHẤT giữa 2 twin: casing field HTTP wire.
//   - qlvb-fe: `pollResult.Data.AutoCompleteDelaySeconds` PascalCase (Newtonsoft qlvb-be)
//   - common-fe: `pollResult.data.autoCompleteDelaySeconds` camelCase (System.Text.Json common-be direct)
// Pure logic (armAutoComplete, tickCountdown, AutoCompleteController) HOÀN TOÀN GIỐNG NHAU.
//
// KONGMING COUNSEL 2 MUST-FIX applied:
//   #1 Deadline-based timer (absolute epoch, robust background tab throttle)
//   #2 Failure path xử lý ở tầng Vue (DangNhap.vue try/catch dismiss overlay)
//   Extra: clamp [0..10], fail-open AllowTotpFallback, guard double-fire, guard Enter conflict

// F1 Deadline động (plan 260818-2208) - TWIN qlvb-fe push-approval-login.js computePollDeadlineMs, KEEP IN
// SYNC. Số ms hard-timeout client dựa remainingSeconds BE (nguồn sự thật TTL). remaining hợp lệ >0 ->
// remaining*1000. Ngược lại (0/âm/NaN/null - attempt legacy ExpiresAt=default, hoặc chưa biết remaining lúc
// poll đầu throw) -> fallback MỀM (default 600000) để KHÔNG đá user tức thì. Hàm KHÔNG đọc field payload nên
// giống hệt qlvb-fe (casing không ảnh hưởng).
export const POLL_FALLBACK_MS = 600000; // fallback mềm khi chưa biết remaining (chốt validate: 600s)
/**
 * @param {number} remainingSeconds remaining BE trả (poll response). <=0/không hợp lệ -> fallback.
 * @param {number} [fallbackMs=POLL_FALLBACK_MS] ms dùng khi remaining không hợp lệ (inject để test).
 * @returns {number} ms cho hard-timeout.
 */
export function computePollDeadlineMs(remainingSeconds, fallbackMs = POLL_FALLBACK_MS) {
  const n = Number(remainingSeconds);
  return Number.isFinite(n) && n > 0 ? n * 1000 : fallbackMs;
}

/**
 * Đọc `autoCompleteDelaySeconds` (camelCase common-be direct) từ poll response. Clamp [0..10] defense-in-depth.
 * Default 0 = auto NGAY (backward compat client cũ + common-be cũ chưa có field).
 * @param {{data?: {autoCompleteDelaySeconds?: number}}} pollResult
 * @returns {number} 0..10
 */
export function extractAutoCompleteDelaySeconds(pollResult) {
  const raw = pollResult && pollResult.data ? pollResult.data.autoCompleteDelaySeconds : null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(10, Math.floor(n)));
}

/**
 * Đọc `allowTotpFallback` (camelCase). Default TRUE fail-open (client/common-be cũ = giữ hành vi hiện tại).
 * @param {{data?: {allowTotpFallback?: boolean}}} pollResult
 * @returns {boolean}
 */
export function extractAllowTotpFallback(pollResult) {
  const raw = pollResult && pollResult.data ? pollResult.data.allowTotpFallback : undefined;
  if (raw === false) return false;
  return true;
}

// Action machine cho auto-complete (giống Phase 4).
export const AutoCompleteAction = Object.freeze({
  COMPLETE_IMMEDIATE: "COMPLETE_IMMEDIATE",
  ARM_COUNTDOWN: "ARM_COUNTDOWN",
});

/**
 * Quyết định arm sau khi poll thấy approved. Pure - deadline tuyệt đối (KONGMING MUST-FIX #1).
 * @param {{data?: {autoCompleteDelaySeconds?: number}}} pollResult
 * @param {number} [nowMs=Date.now()]
 * @returns {{action: string, delaySeconds: number, deadline: number|null, mode: string}}
 */
export function armAutoComplete(pollResult, nowMs = Date.now()) {
  const delay = extractAutoCompleteDelaySeconds(pollResult);
  if (delay === 0) {
    return { action: AutoCompleteAction.COMPLETE_IMMEDIATE, delaySeconds: 0, deadline: null, mode: "auto" };
  }
  return {
    action: AutoCompleteAction.ARM_COUNTDOWN,
    delaySeconds: delay,
    deadline: nowMs + delay * 1000,
    mode: "auto",
  };
}

/**
 * Tick countdown - tính remaining từ deadline tuyệt đối. Robust throttle background tab.
 * @param {{deadline: number, mode?: string}} state
 * @param {number} [nowMs=Date.now()]
 * @returns {{remaining: number, action: "TICK"|"COMPLETE", mode: string}}
 */
export function tickCountdown(state, nowMs = Date.now()) {
  if (!state || typeof state.deadline !== "number") {
    return { remaining: 0, action: "COMPLETE", mode: (state && state.mode) || "auto" };
  }
  const remaining = Math.max(0, Math.ceil((state.deadline - nowMs) / 1000));
  if (remaining <= 0) {
    return { remaining: 0, action: "COMPLETE", mode: state.mode || "auto" };
  }
  return { remaining, action: "TICK", mode: state.mode || "auto" };
}

/**
 * User bấm OK (Enter) trước countdown -> mode="manual". Guard chống Enter conflict form login submit.
 * @param {{deadline?: number}} state
 * @returns {{action: "COMPLETE"|"NOOP", mode: string}}
 */
export function userConfirmComplete(state) {
  if (!state || typeof state.deadline !== "number") {
    return { action: "NOOP", mode: "manual" };
  }
  return { action: "COMPLETE", mode: "manual" };
}

/**
 * User bấm Huỷ (Esc). Nếu `allowTotpFallback=false` -> hiện nút "Đăng nhập ngay" (edge case user không kẹt).
 * @param {boolean} allowTotpFallback
 * @returns {{action: "CANCEL", showLoginNowButton: boolean}}
 */
export function cancelAutoComplete(allowTotpFallback) {
  return {
    action: "CANCEL",
    showLoginNowButton: allowTotpFallback === false,
  };
}

/**
 * Auto-complete controller - deadline-based với visibilitychange listener (KONGMING MUST-FIX #1).
 * Vue Component `DangNhap.vue` inject `onComplete(mode)` callback để gọi complete-login;
 * controller lo tick UI + fire complete đúng lúc deadline.
 */
export class AutoCompleteController {
  constructor({ onTick, onComplete, tickIntervalMs = 1000 } = {}) {
    this._onTick = onTick;
    this._onComplete = onComplete;
    this._tickIntervalMs = tickIntervalMs;
    this._deadline = null;
    this._intervalId = null;
    this._visibilityHandler = null;
    this._completed = false;
  }

  get isArmed() {
    return this._deadline !== null && !this._completed;
  }

  arm(deadline) {
    this.stop();
    this._deadline = deadline;
    this._completed = false;
    this._runTick();
    this._intervalId = setInterval(() => this._runTick(), this._tickIntervalMs);
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      this._visibilityHandler = () => {
        if (!document.hidden) this._runTick();
      };
      document.addEventListener("visibilitychange", this._visibilityHandler);
    }
  }

  _runTick() {
    if (!this.isArmed) return;
    const result = tickCountdown({ deadline: this._deadline, mode: "auto" });
    if (this._onTick) this._onTick(result.remaining);
    if (result.action === "COMPLETE") {
      this._fireComplete("auto");
    }
  }

  _fireComplete(mode) {
    if (this._completed) return;
    this._completed = true;
    const cb = this._onComplete;
    this.stop();
    if (cb) cb(mode);
  }

  confirmManual() {
    if (!this.isArmed) return;
    this._fireComplete("manual");
  }

  cancel() {
    this.stop();
  }

  stop() {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    if (this._visibilityHandler !== null && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._deadline = null;
  }
}
